if (!customElements.get('joffz-portrait-films')) {
  customElements.define('joffz-portrait-films', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.track = this.querySelector('[data-film-track]');
      this.videos = [...this.querySelectorAll('[data-film]')];
      this.ratios = new Map();
      this.activeVideo = null;
      this.playbackMode = null;
      this.mobile = matchMedia('(max-width: 749px)');
      this.hover = matchMedia('(hover: hover) and (pointer: fine)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.hoverPlay = this.dataset.hoverPlay !== 'false';
      this.previous = this.querySelector('[data-film-previous]');
      this.next = this.querySelector('[data-film-next]');
      const options = { signal: this.controller.signal };
      this.sync = this.sync.bind(this);
      this.queueSync = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
          this.updateNavigation();
        });
      };
      this.videos.forEach(video => {
        const media = video.closest('.jpf__media');
        const toggle = media.querySelector('[data-film-toggle]');
        toggle.hidden = false;
        video.muted = true;
        video.addEventListener('playing', () => {
          if (this.activeVideo !== video || document.hidden) {
            this.stop(video);
            return;
          }
          video.dataset.ready = 'true';
          toggle.dataset.playing = 'true';
          toggle.setAttribute('aria-pressed', 'true');
          toggle.setAttribute('aria-label', toggle.dataset.pauseLabel);
        }, options);
        video.addEventListener('pause', () => {
          toggle.dataset.playing = 'false';
          toggle.setAttribute('aria-pressed', 'false');
          toggle.setAttribute('aria-label', toggle.dataset.playLabel);
        }, options);
        video.addEventListener('error', () => {
          video.dataset.ready = 'false';
          media.querySelector('[data-film-error]').hidden = false;
          this.deactivate(video);
        }, options);
        media.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse' && this.hover.matches && this.hoverPlay && !this.reduced.matches && !navigator.connection?.saveData) {
            this.activate(video, 'hover');
          }
        }, options);
        media.addEventListener('pointerleave', event => {
          if (event.pointerType === 'mouse' && this.playbackMode === 'hover') this.deactivate(video);
        }, options);
        let gesture = null;
        let suppressClickUntil = 0;
        media.addEventListener('pointerdown', event => {
          gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, scroll: this.track.scrollLeft, moved: false };
        }, options);
        media.addEventListener('pointermove', event => {
          if (gesture?.id === event.pointerId && Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 12) gesture.moved = true;
        }, { ...options, passive: true });
        media.addEventListener('pointerup', event => {
          if (gesture?.id === event.pointerId && (gesture.moved || Math.abs(this.track.scrollLeft - gesture.scroll) > 12)) suppressClickUntil = performance.now() + 500;
          gesture = null;
        }, options);
        media.addEventListener('pointercancel', () => {
          gesture = null;
          suppressClickUntil = performance.now() + 500;
        }, options);
        media.addEventListener('click', event => {
          if (event.detail !== 0 && performance.now() < suppressClickUntil) return;
          if (video.dataset.playRequested === 'true' || !video.paused) this.deactivate(video);
          else this.activate(video, 'manual');
        }, options);
      });
      if ('IntersectionObserver' in window) {
        this.visibleObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => this.ratios.set(entry.target, entry.intersectionRatio));
          this.queueSync();
        }, { threshold: [0, .05, .15, .25, .5, .75, 1] });
        this.videos.forEach(video => this.visibleObserver.observe(video));
      } else {
        window.addEventListener('scroll', this.queueSync, { ...options, passive: true });
        window.addEventListener('resize', this.queueSync, options);
      }
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', this.queueSync, { ...options, passive: true });
      document.addEventListener('visibilitychange', this.sync, options);
      this.mobile.addEventListener('change', this.queueSync, options);
      this.hover.addEventListener('change', this.sync, options);
      this.reduced.addEventListener('change', this.sync, options);
      window.addEventListener('pagehide', () => this.deactivate(this.activeVideo), options);
      document.addEventListener('joffz:film-play', event => {
        if (event.detail !== this.activeVideo) this.deactivate(this.activeVideo);
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('.jpf__card');
        if (card && this.contains(card)) this.track.scrollTo({ left: card.offsetLeft - this.track.offsetLeft, behavior: 'auto' });
      }, options);
      document.addEventListener('shopify:section:unload', event => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.disconnectedCallback();
      }, options);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.queueSync);
        this.resizeObserver.observe(this.track);
      }
      this.updateNavigation();
    }

    activate(video, mode) {
      if (document.hidden) return;
      this.videos.filter(other => other !== video).forEach(other => this.stop(other));
      this.activeVideo = video;
      this.playbackMode = mode;
      document.dispatchEvent(new CustomEvent('joffz:film-play', { detail: video }));
      video.closest('.jpf__media').querySelector('[data-film-error]').hidden = true;
      this.load(video);
      if (video.error) video.load();
      this.play(video);
    }

    deactivate(video) {
      if (!video) return;
      this.stop(video);
      if (this.activeVideo === video) {
        this.activeVideo = null;
        this.playbackMode = null;
      }
    }

    load(video) {
      if (video.dataset.loaded === 'true') return;
      const sources = [...video.querySelectorAll('source[data-src]')].sort((a, b) => Number(a.dataset.height) - Number(b.dataset.height));
      const target = this.mobile.matches ? 720 : 1080;
      const source = sources.find(item => Number(item.dataset.height) >= target) || sources.at(-1);
      if (!source) return;
      source.src = source.dataset.src;
      video.dataset.loaded = 'true';
      video.preload = 'metadata';
      video.load();
    }

    play(video) {
      this.load(video);
      if (video.dataset.playRequested === 'true' || !video.paused) return;
      const attempt = String(Number(video.dataset.playAttempt || 0) + 1);
      video.dataset.playAttempt = attempt;
      video.dataset.playRequested = 'true';
      const pending = video.play();
      if (pending) pending.then(() => {
        if (video.dataset.playAttempt !== attempt) return;
        if (!this.isConnected || this.activeVideo !== video || video.dataset.playRequested !== 'true') video.pause();
      }).catch(() => {
        if (video.dataset.playAttempt === attempt) this.deactivate(video);
      });
    }

    stop(video) {
      video.dataset.playAttempt = String(Number(video.dataset.playAttempt || 0) + 1);
      video.dataset.playRequested = 'false';
      if (!video.paused) video.pause();
    }

    sync() {
      const video = this.activeVideo;
      if (!this.controller || !video) return;
      const rect = video.getBoundingClientRect();
      const track = this.track.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(rect.right, track.right, innerWidth) - Math.max(rect.left, track.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0));
      const ratio = this.ratios.get(video) ?? visibleWidth * visibleHeight / Math.max(1, rect.width * rect.height);
      const hoverBlocked = this.playbackMode === 'hover' && (!this.hover.matches || this.reduced.matches || navigator.connection?.saveData);
      if (document.hidden || ratio < .15 || hoverBlocked) this.deactivate(video);
    }

    move(direction) {
      const card = this.track.querySelector('.jpf__card');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: this.reduced.matches ? 'auto' : 'smooth' });
    }

    updateNavigation() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.querySelector('[data-film-navigation]').hidden = max < 2;
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max - 2;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.visibleObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.videos?.forEach(video => this.stop(video));
      this.activeVideo = null;
      this.playbackMode = null;
      this.controller = null;
      this.frame = null;
    }
  });
}

